import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

const CHANNEL_META = {
  whatsapp: { label: 'WhatsApp', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  email: { label: 'Email', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
};

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'expired', label: 'Expired' },
];

const pad = (n) => String(n).padStart(2, '0');

// Value for <input type="datetime-local"> -- local wall-clock time, no zone.
function nowForInput() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// sentAt arrives as the wall-clock string 'YYYY-MM-DD HH:mm:ss'; it's shown as
// typed rather than run through a Date zone conversion.
function fmtSentAt(value) {
  const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return '—';
  const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(d);
}

const buildEmptyDraft = () => ({
  channel: 'whatsapp',
  subject: '',
  message: '',
  sentBy: '',
  groupName: '',
  sentAt: nowForInput(),
  isActive: true,
  isExpired: false,
});

function Toggle({ checked, onChange, label, disabled }) {
  return (
    <label className={`inline-flex items-center gap-2 text-xs font-semibold text-gray-600 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={onChange}
        className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${checked ? 'bg-indigo-600' : 'bg-gray-300'}`}
      >
        <span className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </button>
      {label}
    </label>
  );
}

function NoteCard({ note, busy, onToggle, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const meta = CHANNEL_META[note.channel] || CHANNEL_META.whatsapp;
  const isLong = note.message.length > 320 || note.message.split('\n').length > 6;

  return (
    <div className={`rounded-xl border bg-white p-5 shadow-sm ${note.isExpired || !note.isActive ? 'border-gray-200 bg-gray-50/70' : 'border-gray-200'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${meta.badge}`}>{meta.label}</span>
          {note.channel === 'whatsapp' && note.groupName && (
            <span className="inline-flex max-w-[16rem] items-center truncate rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-xs font-semibold text-gray-600" title={note.groupName}>
              Group: {note.groupName}
            </span>
          )}
          {note.isExpired && (
            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">Expired</span>
          )}
          {!note.isActive && (
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600">Inactive</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => onEdit(note)} className="rounded-lg px-2.5 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100">Edit</button>
          <button type="button" onClick={() => onDelete(note)} className="rounded-lg px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-50">Delete</button>
        </div>
      </div>

      {note.channel === 'email' && (
        <h3 className="mt-3 text-base font-semibold text-gray-900">{note.subject}</h3>
      )}

      <p className={`mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-700 ${isLong && !expanded ? 'line-clamp-6' : ''}`}>
        {note.message}
      </p>
      {isLong && (
        <button type="button" onClick={() => setExpanded((v) => !v)} className="mt-1 text-xs font-semibold text-indigo-600 hover:underline">
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-3">
        <div className="text-xs text-gray-500">
          <span className="font-semibold text-gray-700">{note.sentBy || 'Unknown sender'}</span>
          <span className="mx-1.5">·</span>
          {fmtSentAt(note.sentAt)}
        </div>
        <div className="flex items-center gap-4">
          <Toggle label="Active" checked={note.isActive} disabled={busy} onChange={() => onToggle(note, { isActive: !note.isActive })} />
          <Toggle label="Expired" checked={note.isExpired} disabled={busy} onChange={() => onToggle(note, { isExpired: !note.isExpired })} />
        </div>
      </div>
    </div>
  );
}

const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100';

function FieldError({ errors, name }) {
  return errors?.[name] ? <p className="mt-1 text-xs text-red-600">{errors[name][0]}</p> : null;
}

function NoteForm({ initial, saving, errors, onCancel, onSubmit }) {
  const [draft, setDraft] = useState(initial);
  const isEmail = draft.channel === 'email';
  const set = (field) => (e) => setDraft((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(draft); }}
      className="space-y-4"
    >
      <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
        {Object.entries(CHANNEL_META).map(([value, meta]) => (
          <button
            key={value}
            type="button"
            onClick={() => setDraft((prev) => ({ ...prev, channel: value }))}
            className={`rounded-md px-4 py-1.5 text-sm font-semibold ${draft.channel === value ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            {meta.label}
          </button>
        ))}
      </div>

      {isEmail && (
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Subject</label>
          <input className={inputClass} value={draft.subject} onChange={set('subject')} placeholder="Email subject" maxLength={255} />
          <FieldError errors={errors} name="subject" />
        </div>
      )}

      {!isEmail && (
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
            Group name <span className="font-normal normal-case tracking-normal text-gray-400">(optional)</span>
          </label>
          <input className={inputClass} value={draft.groupName} onChange={set('groupName')} placeholder="WhatsApp group this came from" maxLength={255} />
          <FieldError errors={errors} name="groupName" />
        </div>
      )}

      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
          {isEmail ? 'Body' : 'Message'}
        </label>
        <textarea
          className={`${inputClass} min-h-[180px] font-[inherit]`}
          value={draft.message}
          onChange={set('message')}
          placeholder={isEmail ? 'Paste the email body here…' : 'Paste the WhatsApp message here…'}
        />
        <FieldError errors={errors} name="message" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
            {isEmail ? 'Sender email' : 'Sent by'}
          </label>
          <input
            className={inputClass}
            value={draft.sentBy}
            onChange={set('sentBy')}
            placeholder={isEmail ? 'name@example.com' : 'Name or phone number'}
            maxLength={255}
          />
          <FieldError errors={errors} name="sentBy" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Date &amp; time sent</label>
          <input type="datetime-local" className={inputClass} value={draft.sentAt} onChange={set('sentAt')} />
          <FieldError errors={errors} name="sentAt" />
        </div>
      </div>

      <div className="flex flex-wrap gap-6">
        <Toggle label="Active" checked={draft.isActive} onChange={() => setDraft((p) => ({ ...p, isActive: !p.isActive }))} />
        <Toggle label="Expired" checked={draft.isExpired} onChange={() => setDraft((p) => ({ ...p, isExpired: !p.isExpired }))} />
      </div>

      <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
        <button type="button" onClick={onCancel} disabled={saving} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button type="submit" disabled={saving} className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-60">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

export default function RuleNotes() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState(null); // { note: existing | null }
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const resp = await axiosInstance.get('/admin/rule-notes');
      setNotes(resp.data?.notes || []);
    } catch (err) {
      setLoadError(err?.response?.data?.message || 'Failed to load rules & procedures.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeEditor = useCallback(() => {
    if (saving) return;
    setEditor(null);
    setFormErrors(null);
  }, [saving]);

  useEffect(() => {
    if (!editor) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeEditor(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editor, closeEditor]);

  const replaceNote = (saved) => setNotes((prev) => {
    const next = prev.some((n) => n.noteId === saved.noteId)
      ? prev.map((n) => (n.noteId === saved.noteId ? saved : n))
      : [saved, ...prev];
    return next.sort((a, b) => (a.sentAt < b.sentAt ? 1 : a.sentAt > b.sentAt ? -1 : b.noteId - a.noteId));
  });

  const handleSave = async (draft) => {
    setSaving(true);
    setFormErrors(null);
    const body = {
      channel: draft.channel,
      subject: draft.subject,
      message: draft.message,
      sentBy: draft.sentBy,
      groupName: draft.groupName,
      sentAt: draft.sentAt,
      isActive: draft.isActive,
      isExpired: draft.isExpired,
    };
    try {
      const existing = editor?.note;
      const resp = existing
        ? await axiosInstance.put(`/admin/rule-notes/${existing.noteId}`, body)
        : await axiosInstance.post('/admin/rule-notes', body);
      replaceNote(resp.data.note);
      setEditor(null);
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) setFormErrors(data.errors);
      else Swal.fire('Error', data?.message || 'Failed to save.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (note, flags) => {
    setBusyId(note.noteId);
    try {
      const resp = await axiosInstance.patch(`/admin/rule-notes/${note.noteId}/flags`, flags);
      replaceNote(resp.data.note);
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to update.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (note) => {
    const result = await Swal.fire({
      title: 'Delete this entry?',
      text: 'It will be permanently removed. This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`/admin/rule-notes/${note.noteId}`);
      setNotes((prev) => prev.filter((n) => n.noteId !== note.noteId));
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to delete.', 'error');
    }
  };

  const filtered = useMemo(() => {
    const tokens = search.toLowerCase().split(/\s+/).filter(Boolean);
    return notes.filter((n) => {
      if (channelFilter !== 'all' && n.channel !== channelFilter) return false;
      if (statusFilter === 'active' && !n.isActive) return false;
      if (statusFilter === 'inactive' && n.isActive) return false;
      if (statusFilter === 'expired' && !n.isExpired) return false;
      if (!tokens.length) return true;
      const haystack = `${n.subject} ${n.message} ${n.sentBy} ${n.groupName}`.toLowerCase();
      return tokens.every((t) => haystack.includes(t));
    });
  }, [notes, channelFilter, statusFilter, search]);

  const editorInitial = useMemo(() => {
    const note = editor?.note;
    if (!note) return buildEmptyDraft();
    return {
      channel: note.channel,
      subject: note.subject,
      message: note.message,
      sentBy: note.sentBy,
      groupName: note.groupName,
      sentAt: String(note.sentAt || '').slice(0, 16).replace(' ', 'T'),
      isActive: note.isActive,
      isExpired: note.isExpired,
    };
  }, [editor]);

  const tabClass = (on) => `rounded-md px-3 py-1.5 text-sm font-semibold ${on ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`;

  return (
    <DashboardLayout title="Rules & Procedures">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Rules &amp; Procedures</h2>
            <p className="mt-1 text-sm text-gray-500">Super admin only · WhatsApp messages and emails kept for later review.</p>
          </div>
          <button
            type="button"
            onClick={() => { setFormErrors(null); setEditor({ note: null }); }}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
          >
            + Add entry
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search subject, message, sender or group"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 sm:w-72"
          />
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            {[{ value: 'all', label: 'All' }, { value: 'whatsapp', label: 'WhatsApp' }, { value: 'email', label: 'Email' }].map((o) => (
              <button key={o.value} type="button" onClick={() => setChannelFilter(o.value)} className={tabClass(channelFilter === o.value)}>{o.label}</button>
            ))}
          </div>
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            {STATUS_FILTERS.map((o) => (
              <button key={o.value} type="button" onClick={() => setStatusFilter(o.value)} className={tabClass(statusFilter === o.value)}>{o.label}</button>
            ))}
          </div>
        </div>

        {loadError && (
          <div className="flex items-center justify-between rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
            <span>{loadError}</span>
            <button type="button" onClick={load} className="font-semibold underline">Retry</button>
          </div>
        )}

        {loading ? (
          <div className="rounded-xl border-2 border-dashed border-gray-200 p-12 text-center text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-gray-200 p-12 text-center text-gray-500">
            {notes.length === 0
              ? 'Nothing saved yet. Use “Add entry” to paste in a WhatsApp message or an email.'
              : 'No entries match the current filters.'}
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {filtered.map((note) => (
              <NoteCard
                key={note.noteId}
                note={note}
                busy={busyId === note.noteId}
                onToggle={handleToggle}
                onEdit={(n) => { setFormErrors(null); setEditor({ note: n }); }}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {editor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 p-4" role="dialog" aria-modal="true" onClick={closeEditor}>
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-4 text-lg font-semibold text-gray-900">{editor.note ? 'Edit entry' : 'Add entry'}</h3>
            <NoteForm initial={editorInitial} saving={saving} errors={formErrors} onCancel={closeEditor} onSubmit={handleSave} />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
